import { useContext, useEffect, useState } from 'react';
import {
  Tab, Tabs, TabList, TabPanel,
} from 'react-tabs';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import SessionContext from '../../context/SessionContext';
import HeaderInfo from './HeaderInfo/HeaderInfo';
import OrderDetails from './OrderDetails/OrderDetails';
import Address from './Address/Address';
import Quote from './Quote/Quote';
import OrderSummary from './OrderSummary/OrderSummary';
import styles from './Account.module.scss';
import * as utils from '../../utils/OrderUtils';
import Payment from './Payment/Payment';
import PaymentContext from '../../context/PaymentContext';
import '../../translations/i18n';
import TicketDownload from './TicketDownload/TicketDownload';
import TravelDocuments from './TravelDocuments/TravelDocuments';
import TicketsToPost from './TicketsToPost/TicketsToPost';
import PageView from '../Analytics/PageView/PageView';
import { getAppConfig } from '../../config/config';
import useFetchOrder from '../../hooks/useFetchOrder';
import UpdateRequiredDetails from './UpdateRequiredDetails/UpdateRequiredDetails';
import AttributeUpdateContext from '../../context/AttributeUpdateContext';
import Recommendations from './Recommendations/Recommendations';
import { ReactComponent as MagicNavIcon } from '../../svgs/magic-nav.svg';
import { ReactComponent as StarsIcon } from '../../svgs/stars.svg';
import ContactOptions from '../ContactOptions/ContactOptions';
import {
  ERRORS, FLOW_RETURN_STATUS, ORDER_STATUS, PAYMENT_STATUS,
} from '../../constants/constants';
import isFlowPaymentEnabled from '../../utils/isFlowPaymentEnabled';

function Account({ isLoggedIn }) {
  const navigate = useNavigate();
  const config = getAppConfig();

  const loginUrl = utils.prependLangUrlPath(
    '/customer/login',
    config.brand,
    config.language_prefix,
  );

  useEffect(() => {
    if (!isLoggedIn && !sessionStorage.orderId) {
      if (config.brand === 'DSD') {
        window.location.reload();
      } else {
        navigate(loginUrl);
      }
    }
  }, [isLoggedIn]);

  const { t } = useTranslation('common');
  const flowPaymentEnabled = isFlowPaymentEnabled();

  const sessionCtx = useContext(SessionContext);

  const { session } = sessionCtx;

  const orderId = session.loginOrder;
  const sessionId = session.sid;

  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');

  const [cookies, removeCookie] = useCookies('FailedPaymentAttempts');
  const numberOfFailedAttempts = cookies.FailedPaymentAttempts
    ? parseInt(cookies.FailedPaymentAttempts, 10)
    : 0;
  const [isFailedPayment, setIsFailedPayment] = useState(false);

  const paymentCtx = useContext(PaymentContext);
  const {
    paymentStatus, forceFetch, orderUpdate, payAmount,
  } = paymentCtx;

  const { attributeUpdate, attributeStatus, forceAttributeFetch } = useContext(AttributeUpdateContext);

  const { isLoading, serverError, order } = useFetchOrder(
    orderId,
    sessionId,
    orderUpdate,
    paymentStatus,
    forceFetch,
    attributeStatus,
    attributeUpdate,
    forceAttributeFetch,
  );

  // If CCNS or brochure order redirect to login page
  if (order.status === ORDER_STATUS.CUSTOMER_CONTACTED_NO_SALE) {
    const navigationUrl = utils.prependLangUrlPath(
      '/customer/login',
      config.brand,
      config.language_prefix,
    );
    navigate(navigationUrl, { state: { id: 1, orderStatus: order.status } });
  }
  if (serverError) {
    const navigationUrl = utils.prependLangUrlPath(
      '/customer/login',
      config.brand,
      config.language_prefix,
    );
    navigate(navigationUrl, { state: { id: 1, orderStatus: ERRORS.ORDER_STATUS_FETCH_ERROR } });
  }
  if (order.length !== 0 && order.order_tickets.length === 1 && order.order_tickets[0].type_ticket === 'brochure') {
    const navigationUrl = utils.prependLangUrlPath(
      '/customer/login',
      config.brand,
      config.language_prefix,
    );
    navigate(navigationUrl, { state: { id: 1, orderStatus: ORDER_STATUS.BROCHURE_ORDER } });
  }

  const location = useLocation();

  const [isTimedQuote, setIsTimedQuote] = useState(false);

  useEffect(() => {
    if (
      numberOfFailedAttempts >= 3
      || (order.status === ORDER_STATUS.PAYMENT_FAILED && numberOfFailedAttempts >= 1)
    ) {
      setIsFailedPayment(true);
    }
    if (paymentStatus) {
      if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
        setIsTimedQuote(false);
        setSuccessMessage(t('payment.success_msg'));
        setFailureMessage('');
        setIsFlowFlaggedReturn(false);
      } else if (paymentStatus === PAYMENT_STATUS.FAILED) {
        setFailureMessage(t('payment.failure_msg'));
        setSuccessMessage('');
        setIsFlowFlaggedReturn(false);
      }
    }
    const searchParams = new URLSearchParams(location.search);
    const flowStatus = searchParams.get('f');
    const hasLegacySuccessStatus = !flowStatus && location.search.indexOf('success') !== -1;
    const hasLegacyFailStatus = !flowStatus && location.search.indexOf('fail') !== -1;
    if (flowStatus === FLOW_RETURN_STATUS.SUCCESS || hasLegacySuccessStatus) {
      setSuccessMessage(t('payment.success_msg'));
      setFailureMessage('');
      setIsFlowFlaggedReturn(false);
    } else if (flowStatus === FLOW_RETURN_STATUS.FAILED || hasLegacyFailStatus) {
      setFailureMessage(t('payment.failure_msg'));
      setSuccessMessage('');
      setIsFlowFlaggedReturn(false);
    } else if (flowStatus === FLOW_RETURN_STATUS.FLAGGED) {
      setSuccessMessage('');
      setFailureMessage('');
      setIsFlowFlaggedReturn(true);
    }
    if (searchParams.has('f') || location.search.indexOf('success') !== -1 || location.search.indexOf('fail') !== -1) {
      navigate(location.pathname, { replace: true });
    }
    if (attributeStatus) {
      if (attributeStatus === 'attributes-updated') {
        setSuccessMessage(t('attributes.success_msg'));
        setFailureMessage('');
      } else if (attributeStatus === 'attributes-not-updated') {
        setFailureMessage(t('attributes.failure_msg'));
        setSuccessMessage('');
      }
    }
  }, [forceFetch, order]);

  const billingAddress = {
    fname: order.billing_address_fname,
    lname: order.billing_address_lname,
    company: order.billing_address_company,
    street1: order.billing_address_street1,
    street2: order.billing_address_street2,
    city: order.billing_address_city,
    region: order.billing_address_region,
    postal_code: order.billing_address_postal_code,
    country: order.billing_address_country,
    phone: order.billing_address_phone,
    mobile: order.billing_address_mobile,
  };

  const deliveryAddress = {
    fname: order.delivery_address_fname,
    lname: order.delivery_address_lname,
    company: order.delivery_address_company,
    street1: order.delivery_address_street1,
    street2: order.delivery_address_street2,
    city: order.delivery_address_city,
    region: order.delivery_address_region,
    postal_code: order.delivery_address_postal_code,
    country: order.delivery_address_country,
    phone: order.delivery_address_phone,
    mobile: order.delivery_address_mobile,
  };

  const orderTickets = utils.getOrderTickets(order);
  const attributesForEditing = order.status === ORDER_STATUS.QUOTE && utils.getEditableAttributes(order);

  const [quote, setQuote] = useState(false);
  const [ticketsToPost, setTicketsToPost] = useState(false);
  const [ticketsToDownload, setTicketsToDownload] = useState(false);
  const [travelDocuments, setTravelDocuments] = useState(false);
  const [accommodationVouchers, setAccommodationVouchers] = useState([]);
  const vouchers = utils.getVouchers(order, 'all');
  const orderTicketsPost = utils.getOrderTickets(order, 'post');
  const [isPayable, setIsPayable] = useState(true);
  const [isExpiredQuote, setIsExpiredQuote] = useState(false);
  const [isParisPackage, setIsParisPackage] = useState(false);
  const [isFlaggedPayment, setIsFlaggedPayment] = useState(false);
  const [isMissingAttributes, setIsMissingAttributes] = useState(false);
  const [isPaymentFailedTimeLimit, setIsPaymentFailedTimeLimit] = useState(false);
  const [isFlowFlaggedReturn, setIsFlowFlaggedReturn] = useState(false);
  const [isDetailsBeingEdited, setIsDetailsBeingEdited] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isEpicNeedsKioskCollection, setIsEpicNeedsKioskCollection] = useState(false);
  const [isNonRefundableDeposit, setIsNonRefundableDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);
  const isAccommodationOrder = order.order_tickets && order.order_tickets.some((ticket) => Object.keys(ticket.product_categories).includes('1253'));
  const [recommedations, setRecommedations] = useState(false);
  const shouldShowFlaggedPayment = isFlaggedPayment || isFlowFlaggedReturn;

  useEffect(() => {
    const fetchAccommodationVouchers = async () => {
      const response = await utils.getAccommodationVouchers(order);
      if (
        response
        && (response[0].accommodationDetails.length !== 0
          || response[0].tickets.length !== 0)
      ) {
        setAccommodationVouchers(response);
        setTravelDocuments(true);
      }
    };
    fetchAccommodationVouchers();
    if (
      config.brand === 'DSD'
      && orderTickets
      && orderTickets.length > 0
      && order.status === ORDER_STATUS.QUOTE
    ) {
      setQuote(true);
      setTicketsToDownload(false);
      setTicketsToPost(false);
    }
    if (
      config.brand !== 'DSD'
      && orderTickets
      && orderTickets.length > 0
      && order.status === ORDER_STATUS.QUOTE
    ) {
      setQuote(true);
      setTicketsToDownload(false);
      setTicketsToPost(false);
    }
    if (orderTicketsPost && orderTicketsPost.length > 0) {
      setTicketsToPost(true);
    }
    if (order.upsell_data && (order.upsell_data.mealPlans || order.upsell_data.extras)) {
      setRecommedations(true);
    }
  }, [order]);

  useEffect(() => {
    if (vouchers && vouchers.length > 0) {
      // For DSD quotes don't show tickets to download
      if (config.brand === 'DSD' && quote) {
        setTicketsToDownload(false);
      } else {
        setTicketsToDownload(true);
      }
    }
    const ticketAttributes = orderTickets
      && orderTickets
        .map((ticket) => ticket.attributes.map((attribute) => Object.values(attribute)).flat())
        .flat();
    if (
      orderTickets
      && ticketAttributes.includes('Required')
      && order.status === ORDER_STATUS.QUOTE
    ) {
      setIsMissingAttributes(true);
    } else {
      setIsMissingAttributes(false);
    }
  }, [orderTickets]);

  const transportTickets = utils.getTransportTickets(order);
  const isOlderThanSevenDays = (utils.getTimeInUTC() - utils.getTimeInUTC(order.created)) > 604800000;
  const isOlderThan30Minutes = (utils.getTimeInUTC() - utils.getTimeInUTC(order.created)) > 1.8e+6;

  // Check if order should be payable
  useEffect(() => {
    const payDisabledStatus = [ORDER_STATUS.CUSTOMER_CONTACTED_NO_SALE, ORDER_STATUS.CANCELED];
    let quoteExpiryPageRefreshFunction;

    if (payDisabledStatus.includes(order.status)) {
      setIsPayable(false);
    } else if (order.status === ORDER_STATUS.QUOTE) {
      const quoteExpiryDate = utils.getTimeInUTC(order.quote_expiry_date);

      if (utils.getTimeInUTC() < quoteExpiryDate && order.quote_expiry_date !== order.created) {
        // Current time is before expiry date - quote is still valid
        setIsTimedQuote(true);
        setIsExpiredQuote(false);
        quoteExpiryPageRefreshFunction = timerRemount(order.quote_expiry_date);
      } else {
        // Either no expiry window set OR quote has expired
        setIsExpiredQuote(true);
      }
    } else if ((order.status === ORDER_STATUS.IN_CHECKOUT) && utils.getCookieValue('test-CHEC-676-2step-checkout') === '1') {
      if (isOlderThan30Minutes) {
        setIsPayable(false);
      } else {
        const dateObject = new Date(order.created);
        dateObject.setMinutes(dateObject.getMinutes() + 30);
        const refreshTime = dateObject.toISOString();
        quoteExpiryPageRefreshFunction = timerRemount(refreshTime);
      }
    } else if (order.status === ORDER_STATUS.PAYMENT_FAILED) {
      // For Payment Failed order status we use the previous quote logic
      if (isAccommodationOrder) {
        order.order_tickets.every((ticket) => {
          const isParisAccommodation = ticket.product_categories[767];
          const isOrlandoAccommodation = ticket.product_categories[1];
          // If Paris (product category 767) accommodation order or order is more than 7 days old, don't allow payment
          if (isParisAccommodation || isOlderThanSevenDays || (isOrlandoAccommodation && isOlderThan30Minutes)) {
            setIsExpiredQuote(true);
            setIsTimedQuote(false);
            return false;
          }
          // If Orlando (product category 1) accommodation order and less than 7 days old, allow payment
          if (isOrlandoAccommodation && !isOlderThan30Minutes) {
            setIsTimedQuote(true);
            setIsExpiredQuote(false);
            // timerRemount expects an absolute expiry time; the 30-minute window
            // is what gates this branch, so expire when the order crosses it.
            const dateObject = new Date(order.created);
            dateObject.setMinutes(dateObject.getMinutes() + 30);
            quoteExpiryPageRefreshFunction = timerRemount(dateObject.toISOString());
          }
          return true;
        });
      }
    } else if (order.comments_admin && order.comments_admin.some((comment) => comment.message.includes('Online Failed Payment Limit Reached'))) {
      if (
        order.status !== ORDER_STATUS.PAYMENT_FAILED
        && order.status !== ORDER_STATUS.ACTION_REQUIRED
      ) {
        removeCookie('FailedPaymentAttempts', { path: '/' });
        setIsFailedPayment(false);
      } else if (order.status === ORDER_STATUS.ACTION_REQUIRED) {
        if (
          order.order_actions.some((action) => action.type.includes('failed_payment_limit'))
        ) {
          order.order_actions.forEach((action) => {
            if (
              action.type === 'check_payment'
              && action.complete === 0
              && !isFailedPayment
            ) {
              setIsFlaggedPayment(true);
            } else if (
              action.type === 'failed_payment_limit'
              && action.complete === 1
              && isFailedPayment
            ) {
              removeCookie('FailedPaymentAttempts', { path: '/' });
              setIsFailedPayment(false);
            }
          });
        } else {
          removeCookie('FailedPaymentAttempts', { path: '/' });
          setIsFailedPayment(false);
        }
      }
    } else if (order.order_actions && order.order_actions.length !== 0) {
      order.order_actions.forEach((action) => {
        if (
          action.type === 'failed_payment_limit'
          && action.complete === 0
          && !isFailedPayment
        ) {
          setIsFlaggedPayment(true);
        }
      });
    } else {
      setIsPayable(true);
      setIsExpiredQuote(false);
      setIsFlaggedPayment(false);
    }

    if (order.order_tickets) {
      order.order_tickets.forEach((ticket) => {
        // Check if order is Paris package deal or if order is DLP Quote
        if (
          ticket.booking
          && ticket.booking.length > 0
          && ticket.booking[0].room_package_type
          && ticket.destinations[0]
        ) {
          if (
            ticket.booking[0].room_package_type === 'ROOM_TICKET_GEN'
            && ticket.destinations[0].destination_id === '24053'
          ) {
            setIsParisPackage(true);
          }
        }
        // Les Village Nature is always a 2 day ticket.
        if (ticket.attractions[0] && accommodationVouchers[0]) {
          if (ticket.attractions[0].attraction_id === '242389') {
            accommodationVouchers[0].accommodationDetails.ticketDaysDuration = 2;
          }
        }
        // Check if order is Epic Universe Ticket that needs collecting at kiosk
        if (ticket.product_id === '342413') {
          setIsEpicNeedsKioskCollection(true);
        }
      });
    }

    // Check if order status if payment failed and it has been more than 4 hours (1.44e+7 milliseconds is 4 hours)
    if (
      order.status === ORDER_STATUS.PAYMENT_FAILED
      && utils.getTimeInUTC() - utils.getTimeInUTC(order.created) > 1.44e7
    ) {
      setIsPaymentFailedTimeLimit(true);
    }

    // Check if order is non-refundable WDW EBO
    if (
      order.comments_admin
      && order.comments_admin.some((comment) => comment.message.includes('NON REFUNDABLE DEPOSIT WAS SELECTED'))
    ) {
      setIsNonRefundableDeposit(true);
      setDepositAmount(utils.formatPrice(order.currency, order.order_payments[0].amount));
    }
    return quoteExpiryPageRefreshFunction;
  }, [order, accommodationVouchers]);

  // Force a refresh after the payment window has expired, set from the ticket CMS
  const timerRemount = (expiryTime) => {
    const expiry = utils.getTimeInUTC(expiryTime);

    const checkRemount = () => {
      const currentTime = utils.getTimeInUTC();
      // Check if current time has reached or passed expiry time
      if (currentTime >= expiry) {
        clearInterval(intervalId);
        window.location.reload();
      }
    };

    let intervalId;

    // Pass the intervalId to the checkRemount function so it can clear itself
    const check = () => checkRemount(intervalId);

    // Check every second if we should reload the page
    intervalId = setInterval(check, 1000);

    // Return function to clear interval (for cleanup on component unmount)
    return () => clearInterval(intervalId);
  };

  const handleQuoteEdit = () => {
    setIsDetailsBeingEdited(true);
    setTabIndex(1);
  };

  const handleSwitchToRecommendations = () => {
    let index = 0;
    if (quote && order.status === ORDER_STATUS.QUOTE) index += 1;
    if (quote && order.status === ORDER_STATUS.QUOTE && isDetailsBeingEdited) index += 1;
    if (config.brand !== 'DSD' && order.balance > 0 && isPayable) index += 1;
    if (order.status === ORDER_STATUS.PAYMENT_FAILED) index += 1;
    if (travelDocuments) index += 1;

    if (recommedations) {
      setTabIndex(index);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // If quote is expired or payment failed attempts limit is reached load the contact option
  useEffect(() => {
    if (utils.isATBrand() && (isExpiredQuote || isFailedPayment || isPaymentFailedTimeLimit)) {
      let contactTabIndex = 0;

      if (quote && order.status === ORDER_STATUS.QUOTE) contactTabIndex += 1;
      if (quote && order.status === ORDER_STATUS.QUOTE && isDetailsBeingEdited) contactTabIndex += 1;
      if (config.brand !== 'DSD' && order.balance > 0 && isPayable && !isPaymentFailedTimeLimit && !isFailedPayment && !isExpiredQuote) contactTabIndex += 1;
      if (order.status === ORDER_STATUS.PAYMENT_FAILED) contactTabIndex += 1;
      if (travelDocuments) contactTabIndex += 1;
      if (recommedations) contactTabIndex += 1;
      if (ticketsToDownload) contactTabIndex += 1;
      if (ticketsToPost) contactTabIndex += 1;

      setTabIndex(contactTabIndex);
    }
  }, [isExpiredQuote, isFailedPayment, quote, order.status, config.brand, order.balance, isPayable, travelDocuments, recommedations, ticketsToDownload, ticketsToPost, isPaymentFailedTimeLimit]);

  return (
    <div className={styles.account}>
      <PageView userEmail={order.email} orderId={order.id} />
      {!flowPaymentEnabled && (
        <Helmet>
          <script src="https://cdn.checkout.com/js/framesv2.min.js" />
        </Helmet>
      )}
      {serverError && (
        <p className={styles.error}>Error in fetching data ...</p>
      )}
      {isLoading ? (
        <div className={`${styles.loading} loading`} />
      ) : (
        <>
          <HeaderInfo
            orderId={order.id}
            address={billingAddress}
            status={order.customer_order_status}
            departureDate={order.departure_date}
            paid={order.balance <= 0}
            internalStatus={order.status}
            order={order}
            depositDeadlineDays={order.deposit.days}
          />
          {successMessage && <p className={styles.success}>{successMessage}</p>}
          {failureMessage && !isFailedPayment && (
            <p className={styles.error}>{failureMessage}</p>
          )}
          {isFailedPayment && (order.status === ORDER_STATUS.PAYMENT_FAILED || order.status === ORDER_STATUS.ACTION_REQUIRED) && (
            <p className={styles.error}>
              {t(
                'There was a problem processing your payment. Please contact us.',
              )}
            </p>
          )}
          {(order.customer_order_status === ORDER_STATUS.AWAITING_TICKETS) && (
            <div className={styles.warning}>
              <p>
                {t(
                  'Thank you for booking with us. As soon as your theme park tickets for next year have been released by the supplier we will email you to let you know.',
                )}
              </p>
            </div>
          )}
          {isEpicNeedsKioskCollection && (
            <div className={styles.warning}>
              <p>
                {t(
                  'Your Universal 3-Park Explorer tickets will require collection from Will Call before heading to the gates. Please read the ticket voucher carefully.',
                )}
              </p>
            </div>
          )}
          {isNonRefundableDeposit && (
            <div className={styles.warning}>
              <p>
                {t(
                  `Your order was paid with a non-refundable deposit of ${depositAmount}. Please note that if you decide to cancel, this deposit cannot be refunded.`,
                )}
              </p>
            </div>
          )}
          {isTimedQuote && <p className={styles.warning}>{t('Please remember, our pricing and availability is live and subject to change until your reservation is confirmed by the supplier.')}</p>}
          {!isPayable && (order.status === ORDER_STATUS.IN_CHECKOUT) && (utils.getCookieValue('test-CHEC-676-2step-checkout') === '1') && (
          <p className={styles.warning}>
            {(config.brand === 'UO' || config.brand === 'SW') ? t(`Sorry, but due to our live pricing and availability something may have changed. , Please call us on ${config.phone_number} to confirm your order.`) : t('Sorry, but due to our live pricing and availability some things may have changed. To confirm your order, please ')}
            <a href={`/${config.language_prefix}/contact-us`}>{t('contact us.')}</a>
          </p>
          )}
          <div className={styles.summaryChunks}>
            <OrderDetails
              created={order.created}
              total={order.total}
              payments={order.order_payments}
              currency={order.currency}
              discounts={order.order_discounts}
              tickets={order.order_tickets}
              charges={order.order_charges}
              commission={
                config.brand === 'DSD' && order.trade.commission_total
              }
            />

            {/* Billing address hidden, to unhide comment in line below */}
            {/* { (deliveryAddress.street1 !== '') ? <Address billing={billingAddress} delivery={deliveryAddress} /> : <Address billing={billingAddress} /> } */}
          </div>
          <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
            <TabList className={`${styles.tabList} ${styles.mainTabList}`}>
              {quote && order.status === ORDER_STATUS.QUOTE && (
                <Tab className={styles.tab}>{t('payment.quote_tab')}</Tab>
              )}
              {quote && order.status === ORDER_STATUS.QUOTE && isDetailsBeingEdited && (
                <Tab className={styles.tab}>{t('update_required_details')}</Tab>
              )}
              {config.brand !== 'DSD' && order.balance > 0 && isPayable && (!utils.isATBrand() || (!isPaymentFailedTimeLimit && !isFailedPayment && !isExpiredQuote)) && (
                <Tab className={styles.tab}>{t('payment.payment_tab')}</Tab>
              )}
              {order.status === ORDER_STATUS.PAYMENT_FAILED && (
                <Tab className={styles.tab}>
                  {t('payment.order_summary_tab')}
                </Tab>
              )}
              {travelDocuments && (
                <Tab className={styles.tab}>
                  {t('payment.travel_documents_tab')}
                </Tab>
              )}
              {recommedations && (
                <Tab className={`${styles.tab} ${styles.magic}`}>
                  {t('payment.recommendation_tab')}
                  <MagicNavIcon className={styles.magicIcon} />
                  <StarsIcon className={styles.starsIcon} />
                </Tab>
              )}
              {ticketsToDownload && (
                <Tab className={styles.tab}>
                  {t('payment.tickets_download_tab')}
                </Tab>
              )}
              {ticketsToPost && (
                <Tab className={styles.tab}>
                  {t('payment.tickets_post_tab')}
                </Tab>
              )}
              {utils.isATBrand() && (
                <Tab className={styles.tab}>
                  {t('contact_tab')}
                </Tab>
              )}
            </TabList>
            {quote && order.status === ORDER_STATUS.QUOTE && (
              <TabPanel className={styles.tabPanel}>
                <Quote tickets={orderTickets} isTimedQuote={isTimedQuote} onEditDetails={handleQuoteEdit} attributesArray={Boolean(attributesForEditing.length > 0)} balance={order.balance} isAccommodationOrder={isAccommodationOrder} />
              </TabPanel>
            )}
            {quote && order.status === ORDER_STATUS.QUOTE && isDetailsBeingEdited && (
              <TabPanel className={styles.tabPanel}>
                <UpdateRequiredDetails orderId={order.id} setIsDetailsBeingEdited={setIsDetailsBeingEdited} isDetailsBeingEdited={isDetailsBeingEdited} attributes={attributesForEditing} user={order.owner} />
              </TabPanel>
            )}
            {config.brand !== 'DSD' && order.balance > 0 && isPayable && (!utils.isATBrand() || (!isPaymentFailedTimeLimit && !isFailedPayment && !isExpiredQuote)) && (
              <TabPanel className={styles.tabPanel}>
                {shouldShowFlaggedPayment && (
                  <div className={styles.quote}>
                    <p><em>{t('Your payment is Pending.')}</em></p>
                    <br />
                    <p><em>{t('Our operations team will attempt to confirm and authorise the payment within 24 hours. Once this is complete you will receive your order confirmation email. We will be in touch if we require any further information.')}</em></p>
                  </div>
                )}
                {isFailedPayment && (
                  <div className={styles.quote}>
                    <p><em><b>{t('Your payment has failed.')}</b></em></p>
                    <br />
                    <p>
                      <em>
                        {t('Sorry, but you have reached the maximum number of attempts. One of the team will get in touch within 48 hours to try and assist. If you are traveling soon and need help right away, please ')}
                        <a href={`/${config.language_prefix}/contact-us`}>{t('contact us.')}</a>
                      </em>
                    </p>
                  </div>
                )}
                {((isPaymentFailedTimeLimit && !isExpiredQuote && !isFailedPayment) || isExpiredQuote) && (
                  <div className={styles.quote}>
                    <p>
                      {(config.brand === 'UO' || config.brand === 'SW') ? t(`Sorry, but due to our live pricing and availability something may have changed. , Please call us on ${config.phone_number} to confirm your order.`) : t('Sorry, but due to our live pricing and availability some things may have changed. To confirm your order, please ')}
                      <a href={`/${config.language_prefix}/contact-us`}>{t('contact us.')}</a>
                    </p>
                  </div>
                )}
                {isMissingAttributes && !isExpiredQuote && (
                  <div className={styles.quote}>
                    <p><b>{t('Please call us to confirm your Quote.')}</b></p>
                    <br />
                    <p>
                      <em>
                        {t('We need to gather some details from you before your tickets can be booked, so please')}
                        {' '}
                        {(config.brand === 'UO' || config.brand === 'SW') ? t(`contact the team on ${config.phone_number}`) : <a href={`/${config.language_prefix}/contact-us`}>{t('contact us')}</a>}
                        {t(' to confirm your order.')}
                      </em>
                    </p>
                  </div>
                )}
                {isPayable && !isExpiredQuote && !shouldShowFlaggedPayment && !isFailedPayment && !isMissingAttributes && !isPaymentFailedTimeLimit && (
                  <Payment
                    order={order}
                    failureMessage={failureMessage}
                    setFailureMessage={setFailureMessage}
                    payAmount={payAmount}
                    flowPaymentEnabled={flowPaymentEnabled}
                  />
                )}
              </TabPanel>
            )}
            {order.status === ORDER_STATUS.PAYMENT_FAILED && (
              <TabPanel className={styles.tabPanel}>
                <OrderSummary tickets={orderTickets} balance={order.balance} />
              </TabPanel>
            )}
            {travelDocuments && (
              <TabPanel className={styles.tabPanel}>
                <TravelDocuments
                  accommodationVouchers={accommodationVouchers}
                  transport={transportTickets}
                  orderStatus={order.status}
                  isParisPackage={isParisPackage}
                  orderId={order.id}
                  balance={order.balance}
                  onSwitchToRecommendations={handleSwitchToRecommendations}
                  recommedations={recommedations}
                />
              </TabPanel>
            )}
            {recommedations && (
              <TabPanel className={styles.tabPanel}>
                <Recommendations upsellData={order.upsell_data} currency={order.currency} orderId={order.id} />
              </TabPanel>
            )}
            {ticketsToDownload && (
              <TabPanel className={styles.tabPanel}>
                <TicketDownload vouchers={vouchers} orderId={order.id} balance={order.balance} />
              </TabPanel>
            )}
            {ticketsToPost && (
              <TabPanel className={styles.tabPanel}>
                <TicketsToPost tickets={orderTicketsPost} balance={order.balance} />
              </TabPanel>
            )}
            {utils.isATBrand() && (
              <TabPanel className={styles.tabPanel}>
                <ContactOptions isExpiredQuote={isExpiredQuote} isFailedPayment={isFailedPayment} isPaymentFailedTimeLimit={isPaymentFailedTimeLimit} orderStatus={order.status} isOlderThan30Minutes={isOlderThan30Minutes} />
              </TabPanel>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}

export default Account;
